/* Native customer POST. No AJAX, no subscriber suppression, no optimistic success. */
(() => {
  'use strict';
  const readPending = key => {
    try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (_) { return null; }
  };
  const clearPending = key => { try { sessionStorage.removeItem(key); } catch (_) {} };
  const firmTag = value => {
    const normalized = String(value || '').normalize('NFKC').trim().toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '');
    const safe = Array.from(normalized).slice(0, 80).join('').replace(/-+$/g, '');
    return safe ? `gallery_trade_firm_${safe}` : '';
  };
  function initialize(root) {
    if (root.dataset.tpInitialized) return;
    root.dataset.tpInitialized = 'true';
    const form = root.querySelector('form[data-tp-form]');
    const dialog = root.querySelector('dialog[data-tp-dialog]');
    if (!form || !dialog) return;
    // Shopify returns alternate-template submissions to the public page.
    // Persist by locale/path, not by the template-specific section ID.
    const key = `clp-trade-prospectus-pending-${location.pathname}-${root.dataset.language}`;
    const legacyKeys = [`clp-trade-prospectus-pending-${root.dataset.sectionId}`];
    const returnedForm = location.hash.match(/^#clp-trade-request-(template--[0-9]+__trade_prospectus)$/);
    if (returnedForm) legacyKeys.push(`clp-trade-prospectus-pending-${returnedForm[1]}`);
    const groups = {};
    ['email', 'firm', 'country'].forEach(kind => {
      groups[kind] = [...form.querySelectorAll(`[data-tp-${kind}]`)];
      groups[kind].forEach(input => {
        const mirror = () => {
          groups[kind].forEach(other => { if (other !== input) other.value = input.value; other.setCustomValidity(''); });
          syncPayload();
        };
        input.addEventListener('input', mirror);
        input.addEventListener('change', mirror);
      });
    });
    const email = form.querySelector('[data-tp-email-value]');
    if (email?.value) groups.email.forEach(input => { input.value = email.value; });
    const tags = form.querySelector('input[name="contact[tags]"]');
    const buttons = [...form.querySelectorAll('[data-tp-submit]')];
    const design = Boolean(window.Shopify?.designMode);
    const enabled = root.dataset.enabled === 'true' && !design && groups.email.length && groups.country.length && email && tags;
    buttons.forEach(button => { button.disabled = !enabled; button.setAttribute('aria-disabled', String(!enabled)); });

    function syncPayload() {
      if (!email || !tags) return '';
      const address = groups.email.find(input => input.value.trim())?.value.trim() || '';
      const country = groups.country.find(input => input.value)?.value || '';
      groups.email.forEach(input => { input.value = address; });
      groups.country.forEach(input => { input.value = country; });
      email.value = address;
      const language = ['en', 'fr', 'zh'].includes(root.dataset.language) ? root.dataset.language : 'en';
      const allTags = ['newsletter', 'gallery_trade_prospectus', `gallery_newsletter_${language}`,
        `gallery_language_${language}`, `gallery_trade_prospectus_requested_${language}`];
      if (['CA', 'US'].includes(country)) allTags.push(`gallery_trade_country_${country.toLowerCase()}`);
      const company = firmTag(groups.firm.find(input => input.value.trim())?.value);
      if (company) allTags.push(company);
      tags.value = allTags.join(',');
      return country;
    }
    syncPayload();
    // Synchronize before Shopify's document-level CAPTCHA click handlers.
    buttons.forEach(button => button.addEventListener('click', syncPayload, true));

    let previousFocus, previousOverflow;
    const open = () => {
      if (dialog.open || typeof dialog.showModal !== 'function') return;
      previousFocus = document.activeElement;
      previousOverflow = document.body.style.overflow;
      dialog.showModal();
      dialog.scrollTop = 0;
      document.body.style.overflow = 'hidden';
      dialog.querySelector('[data-tp-close]')?.focus({ preventScroll: true });
    };
    dialog.querySelectorAll('[data-tp-close]').forEach(button => button.addEventListener('click', () => dialog.close()));
    // Keep the native PDF link action; dismiss confirmation immediately.
    dialog.querySelectorAll('a.clp-tp__dl').forEach(link => link.addEventListener('click', () => dialog.close()));
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {
      document.body.style.overflow = previousOverflow || '';
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    });

    const pendingKey = [key, ...legacyKeys].find(candidate => readPending(candidate));
    const pending = pendingKey ? readPending(pendingKey) : null;
    const age = pending ? Date.now() - Number(pending.at) : -1;
    const pendingValid = pending && age >= 0 && age < 15 * 60 * 1000 && pending.path === location.pathname;
    const serverSuccess = form.querySelector('[data-tp-server-success]');
    const serverError = form.querySelector('[data-tp-server-error]');
    // Never infer acceptance from customer_posted query parameters or a local flag alone.
    if (!design && enabled && serverSuccess && pendingValid) {
      [key, ...legacyKeys].forEach(clearPending);
      open();
    } else if (pending && (!pendingValid || serverError)) {
      [key, ...legacyKeys].forEach(clearPending);
    }

    form.addEventListener('submit', event => {
      if (!enabled) { event.preventDefault(); return; }
      const country = syncPayload();
      if (!['CA', 'US'].includes(country) || !form.checkValidity()) {
        event.preventDefault(); form.reportValidity(); return;
      }
      try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), path: location.pathname })); } catch (_) {}
      form.querySelectorAll('[data-tp-progress]').forEach(message => { message.hidden = false; });
      // Shopify handles the native POST, CAPTCHA/challenge and success redirect.
      // Keep buttons enabled until navigation: CAPTCHA may defer the submission.
    }, true);

    if (design) {
      root.addEventListener('shopify:block:select', event => {
        if (dialog.contains(event.target)) open();
      });
      root.querySelectorAll('[data-tp-editor-preview]').forEach(button => button.addEventListener('click', open));
    }
  }
  const initializeAll = () => document.querySelectorAll('[data-tp-section]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();
